import {
  FrontendContextV1,
  type CentraProductPage,
} from "@made-people/nestjs-bff-service";
import { ProductTemplate } from "@made-people/blueprint-product";
import { ProductProvider } from "@made-people/grease-product";
import { BreadCrumbsProvider } from "@made-people/grease-bread-crumbs";
import { WashingInstructionsBlock } from "../components/features/washing-instructions-block";
import { SizeGuideTrigger } from "../components/features/size-guide";
import { PDPTrustLogos } from "../components/features/pdp-trust-logos";
import { frontendProductToGreaseRelatedProducts } from "../services/bff/mappers/frontend-product-to-grease-related-products";
import { frontendProductToGreaseProduct } from "../services/bff/mappers/frontend-product-to-grease-product";
import { useAllocatedWarehouseIds } from "../services/centra/allocation-rule/use-allocated-warehouse-ids";
import { useGeneralSettings } from "../hooks/use-general-settings";
import { StoryblokComponent } from "@made-people/grease-storyblok";
import { SbBlokData } from "@storyblok/js";
import { ProductReviewsSneakpeek } from "../components/features/trustpilot/components/product-reviews-sneakpeek";
import { type MmLoaderCustomExtraData } from "../utils/loader-custom-extra-data";
import { useTechnicalFeatures } from "../hooks/use-technical-features-settings";
import { ComfortAccordion } from "../components/features/comfort-accordion/comfort-accordion";
import { ProductFaq } from "../components/features/faq/product-faq";
import { CustomisationDrawerBox } from "../components/comfort-suite/customisation/customisation-drawer-box";
import { FeatureToggles } from "../components/comfort-suite/feature-toggle/feature-toggles";
import { StylePicker } from "../components/comfort-suite/style-picker/style-picker";
import { useTrustpilotPdpData } from "../services/trustpilot/use-trustpilot-pdp-data";
import { TrustpilotReviews } from "../components/features/trustpilot/components/trustpilot-reviews";
import { WashingInstructionDrawer } from "../components/features/washing-instruction-drawer/WashingInstructionDrawer";
import { LowestPriceLast30Days } from "../components/features/lowest-price-last-30-days";
import { PdpBannerForProduct } from "../components/features/pdp-banner/pdp-banner-for-product";

/**
 * A Centra base PDP page.
 */

export const CentraProductPageRoute = (props: {
  page: CentraProductPage;
  frontendContext: FrontendContextV1;
  customExtraData?: MmLoaderCustomExtraData;
}) => {
  const { productData, breadCrumbs } = props.page;
  const generalSettings = useGeneralSettings();
  const { trustpilotProductReviews, trustpilotProductSku } =
    useTrustpilotPdpData();

  const technicalFeatures = useTechnicalFeatures();
  const allocatedWarehouseIds = useAllocatedWarehouseIds();

  const filteredTechnicalFeatures = technicalFeatures.filter(
    ({ heading: technicalFeatureHeading }) =>
      productData?.technical_features?.includes(technicalFeatureHeading),
  );

  const belowTheFoldComponents = [
    ...(generalSettings?.belowTheFoldComponents || []),
  ]?.map((component: SbBlokData, index: number) => (
    <StoryblokComponent key={index} blok={component} />
  ));

  const product = frontendProductToGreaseProduct(
    productData,
    props.frontendContext,
    allocatedWarehouseIds,
  );

  const relatedProducts = frontendProductToGreaseRelatedProducts(
    productData,
    props.frontendContext,
    allocatedWarehouseIds,
  );

  return (
    <ProductProvider
      product={product}
      relatedProducts={relatedProducts}
      context={{ type: "pdp" }}
    >
      <BreadCrumbsProvider breadCrumbs={breadCrumbs}>
        <ProductTemplate
          template="vienna"
          beforeSiblingsSlot={
            <>
              <LowestPriceLast30Days />
              <StylePicker />
              <FeatureToggles />
            </>
          }
          afterSiblingsSlot={
            <>
              <CustomisationDrawerBox />
            </>
          }
          siblingsPickerSettings={{
            template: "color-swatch",
            colorSwatchAttributeKey: "product_color_image_url",
          }}
          reviewStarsComponent={
            <ProductReviewsSneakpeek reviewsData={trustpilotProductReviews} />
          }
          sizeGuideComponentSlot={<SizeGuideTrigger />}
          afterAddToCartSlot={<PDPTrustLogos />}
          afterAttributesSlot={
            <div className="col-start-1 col-span-12 lg:col-span-6 lg:row-start-6">
              <PdpBannerForProduct productSku={productData.productSku} />
              <ComfortAccordion items={filteredTechnicalFeatures ?? []} />
            </div>
          }
          washingInstructionDrawer={<WashingInstructionDrawer />}
          detailsComponentSlot={
            <>
              <WashingInstructionsBlock />
              <ProductFaq
                faq={productData?.faq_product_key}
                product={product}
              />
            </>
          }
          belowTheFoldComponents={
            <div className="col-span-12 lg:col-span-6 col-start-1">
              {belowTheFoldComponents}
            </div>
          }
        />
      </BreadCrumbsProvider>
      <TrustpilotReviews
        type="product"
        productSku={trustpilotProductSku}
        reviewsData={trustpilotProductReviews}
      />
    </ProductProvider>
  );
};
